/**
 * Notification Dropdown Component
 *
 * Displays a list of notifications in a dropdown panel
 * with actions to mark as read and delete
 */

import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Bell,
  Check,
  CheckCheck,
  Trash2,
  FileImage,
  FileText,
  ClipboardList,
  User,
  AlertCircle,
  X,
  Loader2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/presentation/components/ui/Button'
import {
  useNotifications,
  useUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
  useDeleteNotification,
  formatNotificationTime,
} from '@/application/notifications'
import type { Notification, NotificationPriority } from '@/domain/notification'
import { getNotificationRoute, PRIORITY_COLORS } from '@/domain/notification'

// =============================================================================
// Icon Mapping
// =============================================================================

function getEventIcon(event: string) {
  if (event.startsWith('study.')) return FileImage
  if (event.startsWith('report.')) return FileText
  if (event.startsWith('worklist.')) return ClipboardList
  if (event.startsWith('user.')) return User
  return AlertCircle
}

// =============================================================================
// Priority Badge
// =============================================================================

function PriorityBadge({ priority }: { priority: NotificationPriority }) {
  if (priority === 'NORMAL' || priority === 'LOW') return null

  return (
    <span
      className={cn(
        'text-[10px] font-medium px-1.5 py-0.5 rounded',
        PRIORITY_COLORS[priority]
      )}
    >
      {priority === 'URGENT' ? 'URGENTE' : 'ALTA'}
    </span>
  )
}

// =============================================================================
// Notification Item
// =============================================================================

interface NotificationItemProps {
  notification: Notification
  onMarkAsRead: (id: string) => void
  onDelete: (id: string) => void
  onNavigate: (path: string) => void
  isMarkingRead: boolean
  isDeleting: boolean
}

function NotificationItem({
  notification,
  onMarkAsRead,
  onDelete,
  onNavigate,
  isMarkingRead,
  isDeleting,
}: NotificationItemProps) {
  const Icon = getEventIcon(notification.event)
  const route = getNotificationRoute(notification)

  const handleClick = () => {
    if (!notification.isRead) {
      onMarkAsRead(notification.id)
    }
    if (route) {
      onNavigate(route)
    }
  }

  return (
    <div
      className={cn(
        'flex items-start gap-3 p-3 border-b border-[var(--border)] last:border-b-0',
        'hover:bg-[var(--accent)]/50 transition-colors',
        !notification.isRead && 'bg-[var(--primary)]/5',
        route && 'cursor-pointer'
      )}
      onClick={handleClick}
    >
      {/* Icon */}
      <div
        className={cn(
          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
          !notification.isRead
            ? 'bg-[var(--primary)]/10 text-[var(--primary)]'
            : 'bg-[var(--muted)] text-[var(--foreground-muted)]'
        )}
      >
        <Icon className="w-4 h-4" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span
            className={cn(
              'text-sm font-medium truncate',
              !notification.isRead && 'text-[var(--foreground)]'
            )}
          >
            {notification.title}
          </span>
          <PriorityBadge priority={notification.priority} />
        </div>
        <p className="text-xs text-[var(--foreground-muted)] line-clamp-2">
          {notification.message}
        </p>
        <span className="text-[10px] text-[var(--foreground-muted)] mt-1 block">
          {formatNotificationTime(notification.createdAt)}
        </span>
      </div>

      {/* Actions */}
      <div className="flex-shrink-0 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        {!notification.isRead && (
          <button
            className="p-1 rounded hover:bg-[var(--accent)] text-[var(--foreground-muted)] hover:text-[var(--foreground)]"
            onClick={() => onMarkAsRead(notification.id)}
            disabled={isMarkingRead}
            title="Marcar como leída"
          >
            {isMarkingRead ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
          </button>
        )}
        <button
          className="p-1 rounded hover:bg-[var(--destructive)]/10 text-[var(--foreground-muted)] hover:text-[var(--destructive)]"
          onClick={() => onDelete(notification.id)}
          disabled={isDeleting}
          title="Eliminar"
        >
          {isDeleting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Trash2 className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function NotificationDropdown() {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Queries
  const { data: countData } = useUnreadCount()
  const { data: notificationsData, isLoading } = useNotifications({ limit: 10 })

  // Mutations
  const markAsRead = useMarkAsRead()
  const markAllAsRead = useMarkAllAsRead()
  const deleteNotification = useDeleteNotification()

  const unreadCount = countData?.unreadCount ?? 0
  const notifications = notificationsData?.notifications ?? []

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  const handleMarkAsRead = (id: string) => {
    markAsRead.mutate(id)
  }

  const handleMarkAllAsRead = () => {
    markAllAsRead.mutate()
  }

  const handleDelete = (id: string) => {
    deleteNotification.mutate(id)
  }

  const handleNavigate = (path: string) => {
    setIsOpen(false)
    navigate(path)
  }

  return (
    <div ref={dropdownRef} className="relative">
      {/* Bell Button */}
      <button
        className={cn(
          'relative p-2 rounded-lg transition-colors',
          'hover:bg-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]',
          isOpen && 'bg-[var(--accent)]'
        )}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={`Notificaciones${unreadCount > 0 ? ` (${unreadCount} sin leer)` : ''}`}
      >
        <Bell className="w-5 h-5 text-[var(--foreground)]" />
        {unreadCount > 0 && (
          <span
            className={cn(
              'absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1',
              'flex items-center justify-center',
              'text-[10px] font-bold text-white',
              'bg-[var(--destructive)] rounded-full',
              'animate-in zoom-in-50 duration-200'
            )}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className={cn(
            'absolute right-0 top-full mt-2 w-[380px] max-h-[480px]',
            'bg-[var(--background)] border border-[var(--border)] rounded-lg shadow-lg',
            'overflow-hidden z-50',
            'animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200'
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-[var(--border)]">
            <h3 className="font-semibold">Notificaciones</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkAllAsRead}
                  disabled={markAllAsRead.isPending}
                  className="text-xs"
                >
                  {markAllAsRead.isPending ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <CheckCheck className="w-3 h-3 mr-1" />
                  )}
                  Marcar todas
                </Button>
              )}
              <button
                className="p-1 rounded hover:bg-[var(--accent)]"
                onClick={() => setIsOpen(false)}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[380px]">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[var(--foreground-muted)]" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-[var(--foreground-muted)]">
                <Bell className="w-10 h-10 mb-2 opacity-20" />
                <p className="text-sm">No tienes notificaciones</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={handleMarkAsRead}
                  onDelete={handleDelete}
                  onNavigate={handleNavigate}
                  isMarkingRead={markAsRead.isPending && markAsRead.variables === notification.id}
                  isDeleting={
                    deleteNotification.isPending &&
                    deleteNotification.variables === notification.id
                  }
                />
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-2 border-t border-[var(--border)] text-center">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs w-full"
                onClick={() => {
                  setIsOpen(false)
                  navigate('/notifications')
                }}
              >
                Ver todas las notificaciones
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
